import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import FlorealLogo from '@/components/FlorealLogo';
import { useSite } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';

const FALLBACK_NAV = [
    { key: 'home', label: 'Home', to: '/' },
    { key: 'atelier', label: "L'atelier", to: '/atelier' },
    { key: 'matrimoni', label: 'Matrimoni', to: '/matrimoni' },
    { key: 'workshop', label: 'Workshop', to: '/workshop' },
    { key: 'giardini', label: 'Giardini', to: '/giardini' },
    { key: 'galleria', label: 'Galleria', to: '/galleria' },
    { key: 'eventi', label: 'Eventi', to: '/eventi' },
    { key: 'contatti', label: 'Contatti', to: '/contatti' },
];

export default function Header() {
    const { menu, settings } = useSite();
    const navItems = (menu && menu.length ? menu : FALLBACK_NAV).filter((m) => !m.hidden);
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const isHome = pathname === '/';
    const transparent = isHome && !scrolled && !open;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const logoUrl = settings.logo_url_desktop || '';
    const logoMobileUrl = settings.logo_url_mobile || logoUrl;
    // Optional "light" variant of the logo used when the header is transparent
    // (dark backgrounds / hero image). If not provided by CMS, we fall back to
    // a CSS filter that brightens the existing logo so it stays visible.
    const logoLightUrl = settings.logo_url_light || '';
    const logoLightMobileUrl = settings.logo_url_light_mobile || logoLightUrl;
    const activeDesktopSrc = transparent && logoLightUrl ? logoLightUrl : logoUrl;
    const activeMobileSrc = transparent && (logoLightMobileUrl || logoLightUrl) ? (logoLightMobileUrl || logoLightUrl) : logoMobileUrl;
    // Fallback CSS filter when no explicit light logo is uploaded
    const needsFilterHack = transparent && !logoLightUrl && !!logoUrl;
    const logoFilterStyle = needsFilterHack
        ? { filter: 'brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0,0,0,0.35))' }
        : undefined;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [pathname]);

    const resolveLogo = (u) => {
        if (!u) return '';
        if (u.startsWith('http://') || u.startsWith('https://')) return u;
        if (u.startsWith('/')) return u;
        return `${backendUrl}${u}`;
    };

    return (
        <header
            data-testid={TEST_IDS.header}
            className={cn(
                'fixed top-0 left-0 right-0 z-40 transition-colors duration-300',
                transparent
                    ? 'bg-transparent'
                    : 'bg-background/90 backdrop-blur-md border-b border-border',
            )}
        >
            <div className="container-atelier flex items-center justify-between h-20 sm:h-24 gap-4">
                <Link
                    to="/"
                    className="group inline-flex items-center shrink-0"
                    aria-label="Floreal Atelier del Fiore — Home"
                    style={{
                        marginTop: `${settings.logo_margin_top_px || 0}px`,
                        marginBottom: `${settings.logo_margin_bottom_px || 0}px`,
                        marginLeft: `${settings.logo_margin_x_px || 0}px`,
                    }}
                >
                    {logoUrl ? (
                        <>
                            <img
                                src={resolveLogo(activeDesktopSrc)}
                                alt="Floreal"
                                className="hidden sm:block w-auto object-contain transition-[filter] duration-300"
                                style={{
                                    width: `${settings.logo_width_desktop_px || 180}px`,
                                    maxHeight: `${settings.logo_max_height_desktop_px || 80}px`,
                                    ...(logoFilterStyle || {}),
                                }}
                            />
                            <img
                                src={resolveLogo(activeMobileSrc)}
                                alt="Floreal"
                                className="sm:hidden w-auto object-contain transition-[filter] duration-300"
                                style={{
                                    width: `${settings.logo_width_mobile_px || 120}px`,
                                    maxHeight: `${settings.logo_max_height_mobile_px || 56}px`,
                                    ...(logoFilterStyle || {}),
                                }}
                            />
                        </>
                    ) : (
                        <FlorealLogo
                            variant="minimal"
                            className={cn(
                                'text-2xl sm:text-3xl md:text-[2rem] transition-colors duration-300',
                                transparent ? 'text-atelier-ivory drop-shadow-sm' : 'text-atelier-sage-ink',
                            )}
                        />
                    )}
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-6">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.key || item.to}
                            to={item.to}
                            end={item.to === '/'}
                            data-testid={`header-nav-${item.key}-link`}
                            className={({ isActive }) => cn(
                                'text-sm transition-colors whitespace-nowrap',
                                transparent
                                    ? (isActive ? 'text-atelier-ivory' : 'text-atelier-ivory/80 hover:text-atelier-ivory')
                                    : (isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'),
                            )}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Mobile toggle */}
                <button
                    onClick={() => setOpen((v) => !v)}
                    className={cn(
                        'lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-md transition-colors',
                        transparent ? 'text-atelier-ivory hover:bg-foreground/10' : 'hover:bg-secondary',
                    )}
                    data-testid={TEST_IDS.headerMenuToggle}
                    aria-label={open ? 'Chiudi menu' : 'Apri menu'}
                    aria-expanded={open}
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur">
                    <div className="container-atelier py-4 flex flex-col gap-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.key || item.to}
                                to={item.to}
                                end={item.to === '/'}
                                data-testid={`header-nav-${item.key}-link-mobile`}
                                className={({ isActive }) => cn(
                                    'py-3 text-base border-b border-border/40 last:border-0',
                                    isActive ? 'text-foreground' : 'text-muted-foreground',
                                )}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
